import { useEffect, useState } from 'react'
import { ArrowDown, ArrowUp, Check, Plus, X } from 'lucide-react'
import { MAX_GEWINNER_JE_PREIS, restzeit, type RafflePrize } from '@k4ia/shared'
import {
  createPreis,
  deletePreis,
  deletePreisLogo,
  getGewinnspiel,
  preisLogoUrl,
  reorderPreise,
  saveTeilnehmer,
  updatePreis,
  uploadPreisLogo,
  type AdminGewinnspiel,
} from '../lib/api'
import { FileButton } from '../components/ui'
import { liesTeilnehmerliste } from '../lib/teilnehmerliste'

/** Wie oft die Seite den Stand der Ziehung nachliest, solange sie läuft. */
const STAND_POLL_MS = 3000

type PreisEingabe = { company: string; prize: string; winnersCount: number }
type ImportErgebnis = { angenommen: number; doppelt: number; verworfen: string[] }

/**
 * Gewinnspiel zum Opening: Die Preise (eine Firma je Preis, mit Logo, Zahl
 * der Gewinner und eigener Teilnehmerliste aus Instagram). Am Abend lost
 * K4ia auf dem großen Bildschirm aus (`/gewinnspiel`), gesteuert vom Handy
 * über die Adresse mit Token (`/gewinnspiel/go/<token>`) — wie beim Intro.
 */
export function GewinnspielPage() {
  const [daten, setDaten] = useState<AdminGewinnspiel | null>(null)
  const [meldung, setMeldung] = useState<{ art: 'ok' | 'fehler'; text: string } | null>(null)
  const [neueFirma, setNeueFirma] = useState('')
  const [neuerPreis, setNeuerPreis] = useState('')
  const [neueAnzahl, setNeueAnzahl] = useState(1)
  const [busy, setBusy] = useState(false)
  const [importErgebnis, setImportErgebnis] = useState<
    (ImportErgebnis & { preisId: string }) | null
  >(null)

  const laden = async () => setDaten(await getGewinnspiel())

  useEffect(() => {
    void laden().catch((e: unknown) => fehler(e))
  }, [])

  // Während der Ziehung mitlesen, damit der Stand hier stimmt.
  useEffect(() => {
    if (daten?.ziehung.phase !== 'laeuft') return
    const id = setInterval(() => void laden().catch(() => undefined), STAND_POLL_MS)
    return () => clearInterval(id)
  }, [daten?.ziehung.phase])

  function fehler(e: unknown) {
    setMeldung({ art: 'fehler', text: e instanceof Error ? e.message : String(e) })
  }

  async function tun(arbeit: () => Promise<{ preise: RafflePrize[] } | void>, ok?: string) {
    setBusy(true)
    setMeldung(null)
    try {
      const ergebnis = await arbeit()
      if (ergebnis && 'preise' in ergebnis) {
        setDaten((d) => (d ? { ...d, preise: ergebnis.preise } : d))
      } else {
        await laden()
      }
      if (ok) setMeldung({ art: 'ok', text: ok })
    } catch (e) {
      fehler(e)
    } finally {
      setBusy(false)
    }
  }

  async function anlegen() {
    if (!neueFirma.trim()) return
    await tun(() =>
      createPreis({
        company: neueFirma.trim(),
        prize: neuerPreis.trim(),
        winnersCount: neueAnzahl,
      }),
    )
    setNeueFirma('')
    setNeuerPreis('')
    setNeueAnzahl(1)
  }

  async function verschieben(i: number, richtung: -1 | 1) {
    if (!daten) return
    const j = i + richtung
    if (j < 0 || j >= daten.preise.length) return
    const ids = daten.preise.map((p) => p.id)
    const [a] = ids.splice(i, 1)
    ids.splice(j, 0, a!)
    await tun(() => reorderPreise(ids))
  }

  async function listeHochladen(preis: RafflePrize, file: File) {
    setMeldung(null)
    setImportErgebnis(null)
    setBusy(true)
    try {
      const handles = await liesTeilnehmerliste(file)
      if (handles.length === 0) {
        throw new Error(
          'In der Datei stand kein Name. Erwartet wird eine Spalte mit den Instagram-Namen.',
        )
      }
      const ergebnis = await saveTeilnehmer(preis.id, handles)
      setImportErgebnis({ preisId: preis.id, ...ergebnis })
      setDaten((d) => (d ? { ...d, teilnehmer: ergebnis.teilnehmer, preise: ergebnis.preise } : d))
      setMeldung({
        art: 'ok',
        text: `${ergebnis.angenommen} Teilnehmer für „${preis.company}“ übernommen aus „${file.name}“.`,
      })
    } catch (e) {
      fehler(e)
    } finally {
      setBusy(false)
    }
  }

  if (!daten) {
    return (
      <div className="max-w-3xl space-y-8">
        <h2 className="page-title">Gewinnspiel</h2>
        {meldung ? (
          <p role="alert" className="text-sm text-status-error">
            {meldung.text}
          </p>
        ) : (
          <p className="text-sm text-text-muted">Lädt …</p>
        )}
      </div>
    )
  }

  const { ziehung } = daten
  const laeuft = ziehung.phase !== 'wartet'

  return (
    <div className="max-w-3xl space-y-8">
      <div className="space-y-2">
        <h2 className="page-title">Gewinnspiel</h2>
        <p className="text-sm leading-relaxed text-text-muted">
          K4ia lost am Abend auf dem großen Bildschirm aus: Jeder Preis hat seine eigene
          Teilnehmerliste, aus der K4ia so viele Gewinner zieht, wie der Preis hat — alle Preise auf
          einmal, nie zweimal dieselbe Person für einen Preis. Jeder Gezogene hat zehn Minuten, sich
          zu melden; wer am Handy abgehakt wird, hat gewonnen. Läuft die Zeit ab, zieht K4ia für
          dieses Los von selbst neu. Wer sich nicht gemeldet hat, kommt an diesem Abend nicht mehr
          dran.
        </p>
      </div>

      {meldung && (
        <p
          role={meldung.art === 'fehler' ? 'alert' : 'status'}
          className={`text-sm ${meldung.art === 'fehler' ? 'text-status-error' : 'text-text-muted'}`}
        >
          {meldung.text}
        </p>
      )}

      {/* Adressen */}
      <section className="card divide-y divide-[var(--color-border)]">
        <div className="px-5 py-4">
          <h3 className="font-semibold">Bildschirm und Handy</h3>
          <p className="mt-1 text-xs text-text-muted">
            Den Bildschirm auf dem Laptop am großen Screen öffnen und einmal hineinklicken (für den
            Ton). Die Handy-Adresse enthält das Token und gehört nur in die Hände des Teams.
          </p>
        </div>
        <LinkZeile label="Bildschirm" hint="Auf dem Laptop öffnen" url={daten.screenUrl} />
        <LinkZeile label="Handy (Steuerung)" hint="Nur fürs Team" url={daten.remoteUrl} />
        <LinkZeile
          label="Oder: Bühne für alles"
          hint="Ein Bildschirm für Intro, Gespräch und Gewinnspiel; am Handy oben umschalten"
          url={daten.buehne.screenUrl}
        />
        <LinkZeile label="Bühne: Handy" hint="Nur fürs Team" url={daten.buehne.remoteUrl} />
      </section>

      {/* Preise */}
      <section className="space-y-3">
        <div>
          <h3 className="font-semibold">Preise</h3>
          <p className="text-xs text-text-muted">
            Ein Preis je Firma, mit Logo, Zahl der Gewinner und eigener Teilnehmerliste (Excel oder
            CSV mit den Instagram-Namen). In dieser Reihenfolge stehen sie auf dem Bildschirm.
          </p>
        </div>
        {daten.preise.length === 0 && (
          <p className="card p-5 text-sm text-text-muted">Noch kein Preis angelegt.</p>
        )}
        <ol className="space-y-3">
          {daten.preise.map((p, i) => (
            <PreisKarte
              key={p.id}
              preis={p}
              nr={i + 1}
              erster={i === 0}
              letzter={i === daten.preise.length - 1}
              busy={busy}
              gesperrt={laeuft}
              lose={ziehung.preise.filter((z) => z.preisId === p.id)}
              teilnehmer={daten.teilnehmer[p.id] ?? []}
              importErgebnis={importErgebnis?.preisId === p.id ? importErgebnis : null}
              onListe={(file) => listeHochladen(p, file)}
              onHoch={() => void verschieben(i, -1)}
              onRunter={() => void verschieben(i, 1)}
              onSpeichern={(input) => tun(() => updatePreis(p.id, input), 'Gespeichert.')}
              onLoeschen={() => {
                if (window.confirm(`„${p.company}“ mit Preis, Logo und Teilnehmerliste löschen?`))
                  void tun(() => deletePreis(p.id))
              }}
              onLogo={(file) => tun(() => uploadPreisLogo(p.id, file))}
              onLogoEntfernen={() => tun(() => deletePreisLogo(p.id))}
            />
          ))}
        </ol>
        <form
          className="card flex flex-col gap-3 p-4 sm:flex-row sm:items-end sm:p-5"
          onSubmit={(e) => {
            e.preventDefault()
            void anlegen()
          }}
        >
          <label className="block flex-1">
            <span className="mb-1 block text-xs text-text-muted">Firma</span>
            <input
              className="input"
              value={neueFirma}
              onChange={(e) => setNeueFirma(e.target.value)}
              placeholder="z. B. Muster GmbH"
              disabled={laeuft}
            />
          </label>
          <label className="block flex-1">
            <span className="mb-1 block text-xs text-text-muted">
              Preis (was es zu gewinnen gibt)
            </span>
            <input
              className="input"
              value={neuerPreis}
              onChange={(e) => setNeuerPreis(e.target.value)}
              placeholder="z. B. Ein Wochenende im Hotel"
              disabled={laeuft}
            />
          </label>
          <label className="block sm:w-28">
            <span className="mb-1 block text-xs text-text-muted">Gewinner</span>
            <input
              type="number"
              min={1}
              max={MAX_GEWINNER_JE_PREIS}
              className="input text-right tabular-nums"
              value={neueAnzahl}
              onChange={(e) => setNeueAnzahl(anzahlBegrenzen(e.target.value))}
              disabled={laeuft}
            />
          </label>
          <button
            type="submit"
            className="btn-primary"
            disabled={busy || laeuft || !neueFirma.trim()}
          >
            <Plus size={16} aria-hidden="true" />
            Preis anlegen
          </button>
        </form>
        {laeuft && (
          <p className="text-xs text-text-muted">
            Solange die Ziehung läuft, lassen sich Preise und Listen nicht ändern. Zurücksetzen geht
            am Handy.
          </p>
        )}
      </section>

      {/* Stand der Ziehung */}
      <section className="space-y-3">
        <div>
          <h3 className="font-semibold">Ziehung</h3>
          <p className="text-xs text-text-muted">
            {ziehung.phase === 'wartet' && 'Noch nicht gestartet — der Start passiert am Handy.'}
            {ziehung.phase === 'laeuft' &&
              `Läuft seit ${new Date(ziehung.gestartetAm).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr.`}
            {ziehung.phase === 'fertig' && 'Abgeschlossen — alle Gewinner stehen fest.'}
          </p>
        </div>
        {laeuft && (
          <ul className="card divide-y divide-[var(--color-border)]">
            {ziehung.preise.map((z) => {
              const p = daten.preise.find((x) => x.id === z.preisId)
              return (
                <li key={z.id} className="flex items-center gap-3 px-5 py-3 text-sm">
                  <span className="min-w-0 flex-1 truncate">
                    {p?.company ?? z.preisId}
                    {p && p.winnersCount > 1 ? ` · ${z.los + 1} von ${p.winnersCount}` : ''}
                  </span>
                  <span className="font-mono">
                    {z.gewinner ? `@${z.gewinner}` : '— niemand übrig —'}
                  </span>
                  {z.bestaetigtAm ? (
                    <span className="inline-flex items-center gap-1 text-status-active">
                      <Check size={16} aria-hidden="true" /> da
                    </span>
                  ) : z.gewinner ? (
                    <span className="font-mono text-text-muted">
                      {restzeit(z.fristBis, Date.now())}
                    </span>
                  ) : null}
                </li>
              )
            })}
            {ziehung.nichtDa.length > 0 && (
              <li className="px-5 py-3 text-xs text-text-muted">
                Nicht gemeldet: {ziehung.nichtDa.map((h) => `@${h}`).join(', ')}
              </li>
            )}
          </ul>
        )}
      </section>
    </div>
  )
}

function anzahlBegrenzen(wert: string): number {
  return Math.max(1, Math.min(MAX_GEWINNER_JE_PREIS, Number(wert) || 1))
}

function PreisKarte({
  preis,
  nr,
  erster,
  letzter,
  busy,
  gesperrt,
  lose,
  teilnehmer,
  importErgebnis,
  onListe,
  onHoch,
  onRunter,
  onSpeichern,
  onLoeschen,
  onLogo,
  onLogoEntfernen,
}: {
  preis: RafflePrize
  nr: number
  erster: boolean
  letzter: boolean
  busy: boolean
  gesperrt: boolean
  lose: { gewinner: string | null; bestaetigtAm: number | null }[]
  teilnehmer: string[]
  importErgebnis: ImportErgebnis | null
  onListe: (file: File) => Promise<void>
  onHoch: () => void
  onRunter: () => void
  onSpeichern: (input: PreisEingabe) => Promise<void>
  onLoeschen: () => void
  onLogo: (file: File) => Promise<void>
  onLogoEntfernen: () => Promise<void>
}) {
  const [company, setCompany] = useState(preis.company)
  const [prize, setPrize] = useState(preis.prize)
  const [anzahl, setAnzahl] = useState(preis.winnersCount)
  useEffect(() => {
    setCompany(preis.company)
    setPrize(preis.prize)
    setAnzahl(preis.winnersCount)
  }, [preis.company, preis.prize, preis.winnersCount])
  const geaendert =
    company !== preis.company || prize !== preis.prize || anzahl !== preis.winnersCount
  const gezogen = lose.filter((l) => l.gewinner).map((l) => `@${l.gewinner}`)

  return (
    <li className="card space-y-3 p-4 sm:p-5" data-testid="preis">
      <div className="flex items-center gap-1 sm:gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-2 font-mono text-sm">
          {nr}
        </span>
        <div className="flex h-12 w-24 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white">
          {preis.logoKey ? (
            <img
              src={preisLogoUrl(preis.id, preis.logoKey)}
              alt={`Logo ${preis.company}`}
              className="max-h-10 max-w-[5.5rem] object-contain"
            />
          ) : (
            <span className="text-[0.65rem] text-neutral-400">kein Logo</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{preis.company}</p>
          {preis.winnerHandle ? (
            <p className="text-xs text-status-active">
              Gewinner:{' '}
              {preis.winnerHandle
                .split(', ')
                .map((h) => `@${h}`)
                .join(', ')}
            </p>
          ) : gezogen.length > 0 ? (
            <p className="text-xs text-text-muted">gezogen: {gezogen.join(', ')}</p>
          ) : (
            <p className="truncate text-xs text-text-muted">
              {preis.prize || 'Preis nicht beschrieben'}
              {preis.winnersCount > 1 ? ` · ${preis.winnersCount} Gewinner` : ''}
            </p>
          )}
        </div>
        <button
          type="button"
          aria-label="Nach oben"
          disabled={erster || busy || gesperrt}
          onClick={onHoch}
          className="flex h-11 w-11 items-center justify-center rounded-full text-text-muted hover:text-text disabled:opacity-30"
        >
          <ArrowUp size={18} />
        </button>
        <button
          type="button"
          aria-label="Nach unten"
          disabled={letzter || busy || gesperrt}
          onClick={onRunter}
          className="flex h-11 w-11 items-center justify-center rounded-full text-text-muted hover:text-text disabled:opacity-30"
        >
          <ArrowDown size={18} />
        </button>
        <button
          type="button"
          aria-label="Preis löschen"
          disabled={busy || gesperrt}
          onClick={onLoeschen}
          className="flex h-11 w-11 items-center justify-center rounded-full text-text-muted hover:text-status-error disabled:opacity-30"
        >
          <X size={18} />
        </button>
      </div>
      <div className="grid gap-3 sm:grid-cols-[1fr_1fr_7rem]">
        <label className="block">
          <span className="mb-1 block text-xs text-text-muted">Firma</span>
          <input
            className="input"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            disabled={gesperrt}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-text-muted">Preis</span>
          <input
            className="input"
            value={prize}
            onChange={(e) => setPrize(e.target.value)}
            disabled={gesperrt}
            placeholder="Was es zu gewinnen gibt"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-text-muted">Gewinner</span>
          <input
            type="number"
            min={1}
            max={MAX_GEWINNER_JE_PREIS}
            className="input text-right tabular-nums"
            value={anzahl}
            onChange={(e) => setAnzahl(anzahlBegrenzen(e.target.value))}
            disabled={gesperrt}
            aria-label={`Gewinner bei ${preis.company}`}
          />
        </label>
      </div>
      {anzahl > 1 && (
        <p className="-mt-1 text-xs text-text-muted">
          {anzahl} Gewinner aus derselben Liste — nie dieselbe Person zweimal.
        </p>
      )}

      {/* Teilnehmerliste dieses Preises */}
      <div className="space-y-2 rounded-xl bg-surface-2 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <FileButton
            label={teilnehmer.length > 0 ? 'Teilnehmerliste ersetzen' : 'Teilnehmerliste hochladen'}
            accept=".xlsx,.xlsm,.csv,.txt,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            disabled={gesperrt}
            onFile={onListe}
          />
          <p className="text-sm" data-testid="teilnehmer-anzahl">
            <strong>{teilnehmer.length}</strong> Teilnehmer
            {teilnehmer.length === 0 && (
              <span className="text-status-error"> — ohne Liste kann nicht gezogen werden</span>
            )}
          </p>
        </div>
        {importErgebnis && (importErgebnis.doppelt > 0 || importErgebnis.verworfen.length > 0) && (
          <p className="text-xs text-text-muted">
            {importErgebnis.doppelt > 0 && `${importErgebnis.doppelt} doppelt. `}
            {importErgebnis.verworfen.length > 0 &&
              `Nicht übernommen (kein Instagram-Name): ${importErgebnis.verworfen.join(', ')}`}
          </p>
        )}
        {teilnehmer.length > 0 && (
          <details>
            <summary className="cursor-pointer text-xs text-text-muted">Liste anzeigen</summary>
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {teilnehmer.map((h) => (
                <li key={h} className="rounded-full bg-surface px-2.5 py-1 font-mono text-xs">
                  @{h}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <FileButton
          label={preis.logoKey ? 'Logo ersetzen' : 'Logo hochladen'}
          accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
          hint="PNG mit transparentem Hintergrund am besten, unter 2 MB, kein SVG"
          disabled={gesperrt}
          onFile={onLogo}
        />
        {preis.logoKey && (
          <button
            type="button"
            className="btn-chip"
            disabled={busy || gesperrt}
            onClick={() => void onLogoEntfernen()}
          >
            Logo entfernen
          </button>
        )}
        {geaendert && (
          <button
            type="button"
            className="btn-primary ml-auto"
            disabled={busy || gesperrt || !company.trim()}
            onClick={() =>
              void onSpeichern({
                company: company.trim(),
                prize: prize.trim(),
                winnersCount: anzahl,
              })
            }
          >
            Speichern
          </button>
        )}
      </div>
    </li>
  )
}

function LinkZeile({ label, hint, url }: { label: string; hint: string; url: string }) {
  return (
    <div className="flex flex-col gap-1 px-5 py-4">
      <span className="text-[0.95rem] font-medium text-text">{label}</span>
      <span className="text-xs text-text-muted">{hint}</span>
      <span className="flex items-center gap-2">
        <code className="min-w-0 flex-1 break-all font-mono text-sm">{url}</code>
        <button
          type="button"
          className="btn-chip shrink-0"
          onClick={() => void navigator.clipboard?.writeText(url)}
        >
          Kopieren
        </button>
      </span>
    </div>
  )
}
